import React from "react";
import { FaSearch } from "react-icons/fa";
import "../../../asset/new/css/admin-modern.css";

/**
 * DataTable - Reusable table component for admin lists
 * 
 * @param {string} title - Table title
 * @param {number} totalCount - Total number of records
 * @param {array} columns - Column definitions [{key, label, sortable, render, width}]
 * @param {array} data - Table data
 * @param {object} pagination - Pagination data
 * @param {function} onSearch - Search handler
 * @param {function} onSort - Sort handler
 * @param {function} onPageChange - Page change handler
 * @param {node} actions - Action buttons (e.g., Export)
 * @param {node} emptyState - Custom empty state
 * @param {boolean} loading - Loading state
 */
const DataTable = ({
  title,
  totalCount = 0,
  columns = [],
  data = [],
  pagination,
  onSearch,
  onSort,
  onPageChange,
  actions,
  emptyState,
  loading = false,
  searchPlaceholder = "Search..."
}) => {
  // Calculate visible page range
  let startPage = 1;
  let endPage = 1;

  if (pagination) {
    startPage = Math.max(1, pagination.page - 2);
    endPage = Math.min(pagination.totalPages, pagination.page + 2);

    if (pagination.page <= 3) {
      endPage = Math.min(5, pagination.totalPages);
    } else if (pagination.page >= pagination.totalPages - 2) {
      startPage = Math.max(1, pagination.totalPages - 4);
    }
  }

  return (
    <div className="admin-table-wrapper">
      {/* Table Header */}
      <div className="admin-table-header">
        <h3 className="admin-table-title">
          {title} {totalCount > 0 && `(${totalCount})`}
        </h3>
        <div className="admin-table-actions">
          {onSearch && (
            <div className="admin-table-search">
              <FaSearch className="admin-table-search-icon" size={14} />
              <input
                type="search"
                placeholder={searchPlaceholder}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
          )}
          {actions}
        </div>
      </div>

      {/* Table Container */}
      <div className="admin-table-container">
        {loading ? (
          <div className="admin-loading">
            <div className="admin-spinner"></div>
          </div>
        ) : data.length > 0 ? (
          <table className="admin-table">
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    style={{ 
                      width: column.width,
                      cursor: column.sortable ? 'pointer' : 'default'
                    }}
                    onClick={() => column.sortable && onSort?.(column.key)}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr key={row.id || rowIndex}>
                  {columns.map((column, colIndex) => (
                    <td key={colIndex}>
                      {column.render 
                        ? column.render(row, rowIndex) 
                        : row[column.key] || 'N/A'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          emptyState || (
            <div className="admin-empty-state">
              <div className="admin-empty-state-icon">
                <FaSearch />
              </div>
              <h3 className="admin-empty-state-title">No Data Found</h3>
              <p className="admin-empty-state-description">
                No records match your criteria
              </p>
            </div>
          )
        )}
      </div>

      {/* Table Footer - Pagination */}
      {pagination && data.length > 0 && (
        <div className="admin-table-footer">
          <div className="admin-table-info">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
            {Math.min(pagination.page * pagination.limit, pagination.totalResults)} of{" "}
            {pagination.totalResults} results
          </div>
          <div className="admin-pagination">
            <button
              className="admin-pagination-item"
              onClick={() => onPageChange?.(pagination.prevPage)}
              disabled={!pagination.prevPage}
            >
              Previous
            </button>
            {Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i
            ).map((page) => (
              <button
                key={page}
                className={`admin-pagination-item ${
                  pagination.page === page ? "active" : ""
                }`}
                onClick={() => onPageChange?.(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="admin-pagination-item"
              onClick={() => onPageChange?.(pagination.nextPage)}
              disabled={!pagination.nextPage}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
